import { useState, useEffect, useCallback } from "react";
import moment from "moment";
import axios from "axios";
import { apiUrl } from "features/configure";
import { useOutletContext, useParams } from "react-router";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { selectSubEntities } from "features/metadesk/redux/entityConfig/entityConfig.selector";
import { sortArrayByKey } from "features/helpers/utils";
import { Box } from "@mui/material";
import { IconButton } from "@metacrm/metacrm-material-ui/dist/Button";
import { useTheme } from "@mui/styles";
import useNavigateEntity from "hooks/useNavigateEntity";
import { StyledStatus } from "../NotificationCenter/NotificationCenter.styles";
import { enqueueSnackbar } from "features/common/redux/actions";
import { get, isNumber, debounce } from "lodash-es";
import useTableParams from "hooks/useTableParams";
import {
  formatNumberWithComma,
  formatPercentage,
  transformRate,
} from "features/helpers/utils";
import { StyledBlurBox } from "features/metadesk/Notification/NotificationReport/component/ChannelPerformance/ChannelPerformance.styles";

const useNotificationCenter = () => {
  const { navigateEntity } = useNavigateEntity();
  const [entityName] = useOutletContext();
  const dispatch = useDispatch();

  const theme = useTheme();
  const { formatMessage } = useIntl();
  const [notificationList, setNotificationList] = useState([]);
  const [channelPerformance, setChannelPerformance] = useState({});
  const [loading, setLoading] = useState(false);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [refetchData, setRefetchData] = useState(false);

  const [actionsList, setActionsList] = useState([]);
  const [isFeatureLocked, setIsFeatureLocked] = useState("comingSoon"); // comingSoon  setUpFootprints

  // 使用 useTableParams 管理篩選條件
  const { params, setParam, setParams } = useTableParams({
    page: 1,
    order: "desc",
    orderBy: "",
    searchText: "",
    dateRange: [
      moment().utc().startOf("day").subtract(6, "days").valueOf(),
      moment().utc().endOf("day").valueOf(),
    ],
  });

  const { page, order, orderBy, searchText, dateRange } = params;

  const fetchOverviewData = useCallback(
    debounce(async () => {
      setOverviewLoading(true);
      try {
        const data = await axios.get(
          `${apiUrl}/api/notification/performance/${entityName}/overview`,
          {
            params: {
              search: searchText,
              startDate: dateRange[0],
              endDate: dateRange[1],
            },
          }
        );

        setChannelPerformance(data?.data);
      } catch (error) {
        dispatch(
          enqueueSnackbar({
            message: get(error, "response.data.error", "error"),
          })
        );
      } finally {
        setOverviewLoading(false);
      }
    }, 500),
    [entityName, searchText, dateRange]
  );

  const fetchTableData = useCallback(
    debounce(async () => {
      setLoading(true);
      try {
        const data = await axios.get(
          `${apiUrl}/api/notification/performance/${entityName}/table`,
          {
            params: {
              search: searchText,
              sort: orderBy,
              order: order,
              page: page,
              startDate: dateRange[0],
              endDate: dateRange[1],
            },
          }
        );

        setTotalItems(get(data, "headers.x-total-count"));
        setNotificationList(data.data);
      } catch (error) {
        dispatch(
          enqueueSnackbar({
            message: get(error, "response.data.error", "error"),
          })
        );
      } finally {
        setLoading(false);
      }
    }, 500),
    [entityName, searchText, dateRange, page, order, orderBy]
  );

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setParams({ order: isAsc ? "desc" : "asc", orderBy: property });
  };

  const handleClickActions = (event, itemData) => {
    event.stopPropagation();
    const queryString = window.location.search;

    setActionsList([
      {
        children: (
          <Box>{formatMessage({ id: "notification.report.viewDetail" })}</Box>
        ),
        func: (e) =>
          navigateEntity(
            `/notification/notification-detail/${itemData._id}${queryString}`
          ),
      },
    ]);

    setAnchorEl(event.currentTarget);
  };

  const handleCloseActions = () => {
    setAnchorEl(null);
  };

  const renderBlockColumn = useCallback((itemData) => {
    return (
      <Box slot="notificationTitle">
        <StyledStatus>{itemData.notificationTitle}</StyledStatus>
      </Box>
    );
  }, []);

  const renderOpened = (itemData) => {
    if (itemData.channel === "widget" || itemData.channel === "email") {
      return isNumber(itemData.opened / itemData.sent)
        ? formatPercentage(itemData.opened / itemData.sent)
        : "-";
    } else {
      return "-";
    }
  };

  const renderContents = useCallback(
    (itemData) => {
      const contents = [
        <Box slot="notificationTitle">
          <StyledStatus>{itemData.notificationTitle}</StyledStatus>
        </Box>,
        <Box slot="channel">{itemData.channel}</Box>,
        <Box slot="sendTime">
          {moment(itemData.sendTime).format("MMM D, YYYY HH:mm")}
        </Box>,
        <Box slot="channel">{itemData.channel}</Box>,
        <Box slot="sendTime">
          {moment(itemData.sendTime).format("MMM D, YYYY HH:mm")}
        </Box>,
        <Box slot="sent">
          {itemData.sent ? formatNumberWithComma(itemData.sent) : "-"}
        </Box>,
        <Box slot="opened">{renderOpened(itemData)}</Box>,
        <StyledBlurBox isLocked={isFeatureLocked} slot="clicked">
          {isFeatureLocked
            ? "N/A"
            : isNumber(itemData.clicked / itemData.sent)
            ? formatPercentage(itemData.clicked / itemData.sent)
            : "-"}
        </StyledBlurBox>,
        <StyledBlurBox isLocked={isFeatureLocked} slot="walletConnected">
          {isFeatureLocked
            ? "N/A"
            : isNumber(itemData.walletConnected / itemData.sent)
            ? formatPercentage(itemData.walletConnected / itemData.sent)
            : "-"}
        </StyledBlurBox>,
        <StyledBlurBox isLocked={isFeatureLocked} slot="objective">
          {isFeatureLocked
            ? "N/A"
            : isNumber(itemData.objectiveAchieved / itemData.sent)
            ? formatPercentage(itemData.objectiveAchieved / itemData.sent)
            : "-"}
        </StyledBlurBox>,
        <Box slot="actions">
          {" "}
          <IconButton
            onClick={(e) => handleClickActions(e, itemData)}
            size={24}
            color={theme.customColors.purple[500]}
          >
            <i className="meta-crm-icon-ic_dots font-size-24" />
          </IconButton>
        </Box>,
      ];

      return contents;
    },
    [isFeatureLocked]
  );

  useEffect(() => {
    fetchTableData();
    return () => fetchTableData.cancel();
  }, [searchText, dateRange, page, order, orderBy]);

  useEffect(() => {
    fetchOverviewData();
    return () => fetchOverviewData.cancel();
  }, [searchText, dateRange]);

  useEffect(() => {
    const fetchDataAsync = async () => {
      if (refetchData) {
        await fetchTableData();
        setRefetchData(false);
      }
    };
    fetchDataAsync();
  }, [refetchData]);

  const handleChangePage = (newPage) => {
    // setPage(newPage);
    setParams({ page: newPage });
  };

  const handleSearchChange = (event) => {
    setParams({ searchText: event.target.value, page: 1 });
  };

  const setDateRange = (event) => {
    setParams({ dateRange: event, page: 1 });
  };

  return {
    notificationList,
    channelPerformance,
    setNotificationList,
    isFeatureLocked,
    renderContents,
    searchText,
    page,
    dateRange,
    totalItems,
    handleChangePage,
    handleSearchChange,
    setDateRange,
    order,
    orderBy,
    handleRequestSort,
    loading,
    anchorEl,
    actionsList,
    handleCloseActions,
  };
};

export default useNotificationCenter;
